// Placing the permit QR by dragging it, shared by the feed builder and the
// post builder.
//
// The arithmetic is the whole reason this is a module. The saved position
// is normalised against the creative's PHOTO area, not its canvas — and on
// a 9:16 with a gallery rail the photo stops short of the bottom. So the
// drag handle has to convert between hero space and card space in both
// directions, and getting the direction wrong puts the handle ~28% away
// from the code it is supposed to be sitting on.
//
// At 1:1 hero === canvas and every ratio below collapses to 1, so the same
// functions serve both without a special case.

export interface QrGeometry {
  /** QR edge length in canvas px. */
  qrSize: number;
  /** Full canvas height in px (1080 square, 1920 vertical). */
  canvasH: number;
  /** Photo height in px — shorter than the canvas when a rail renders. */
  heroH: number;
  /** Canvas width in px. Fixed at 1080 across ratios. */
  canvasW?: number;
}

export interface QrPosition {
  x: number;
  y: number;
}

/** Fraction of the hero the code covers on each axis. */
function fractions({ qrSize, heroH, canvasW = 1080 }: QrGeometry) {
  return { fx: qrSize / canvasW, fy: qrSize / heroH };
}

/**
 * CSS percentages placing the drag handle over the rendered code.
 *
 * `y` is stored in hero space, so it is multiplied back by the hero's
 * share of the card to land in the same place the template drew it.
 */
export function qrHandleStyle(pos: QrPosition, geo: QrGeometry) {
  const { fx, fy } = fractions(geo);
  const heroOfCard = geo.heroH / geo.canvasH;
  return {
    left: `${pos.x * 100}%`,
    top: `${pos.y * heroOfCard * 100}%`,
    width: `${fx * 100}%`,
    height: `${fy * heroOfCard * 100}%`,
  };
}

/**
 * Pointer position -> a stored QR position, clamped to the photo.
 *
 * Clamping to the hero rather than the canvas is deliberate: past the
 * hero's lower edge the code would sit on the rail, not on the image.
 */
export function qrFromPointer(
  clientX: number,
  clientY: number,
  rect: DOMRect,
  geo: QrGeometry,
): QrPosition {
  const { fx, fy } = fractions(geo);
  const heroOfCard = geo.heroH / geo.canvasH;
  const x = (clientX - rect.left) / rect.width - fx / 2;
  // Divide by heroOfCard to express a fraction of the CARD as a fraction
  // of the hero, which is the space the value is stored in.
  const y = (clientY - rect.top) / rect.height / heroOfCard - fy / 2;
  return {
    x: Math.min(Math.max(x, 0), 1 - fx),
    y: Math.min(Math.max(y, 0), 1 - fy),
  };
}
